import FootTrafficFilterApi from '@/Enums/Attribution/FootTraffic/FilterApi.enum';
import Color from '@/Enums/Color.enum';
import TotalMetric from '@/Enums/ReportingDashboard/TotalMetric.enum';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import FootTrafficService from '@/Services/FootTraffic/FootTraffic.service';
import MegaphoneIcon from '@/components/Icons/MegaphoneIcon';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectFootTrafficTotals,
  selectFootTrafficTotalsChannels,
} from '@/store/selectors/attribution/footTraffic.selectors';
import { setChannels } from '@/store/slices/attribution/footTraffic.slice';
import {
  Box,
  Checkbox,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  selectClasses,
} from '@mui/material';
import { useCallback, useMemo } from 'react';

const options = Object.entries(FootTrafficService.channelsToImpressionMetrics).map(([channel, metric]) => ({
  value: channel,
  label: metric,
})) as IControlOption<string, FootTrafficFilterApi>[];

interface IProps {
  disabled?: boolean;
}

const TooltipContent =
  'Represents the total measured impressions across selected channels, filtered by date, advertiser, and campaign.';

const FootTrafficImpressionsTotals = ({ disabled }: IProps) => {
  const dispatch = useAppDispatch();
  const { totals, loading } = useAppSelector(selectFootTrafficTotals);
  const channels = useAppSelector(selectFootTrafficTotalsChannels);

  const onChange = useCallback(
    (event: SelectChangeEvent<FootTrafficFilterApi[]>) => {
      dispatch(setChannels(event.target.value as FootTrafficFilterApi[]));
    },
    [dispatch],
  );

  const totalImpressions = useMemo(
    () => numberToUsFormat(totals[TotalMetric.Impressions]),
    [totals[TotalMetric.Impressions]],
  );

  const getChecked = useCallback((channel: FootTrafficFilterApi) => channels.includes(channel), [channels]);

  const channelSubLabel = useMemo(() => {
    if (channels.length === 0) return '';
    if (channels.length === options.length) return 'All Channels';
    return channels
      .map(ch => FootTrafficService.channelsToImpressionMetrics[ch].replace(' Impressions', ''))
      .join(' + ');
  }, [channels]);

  return (
    <Paper className='flex flex-1 print:p-[10px] flex-col p-[22px] pb-[28px]'>
      <Select
        multiple
        value={channels}
        displayEmpty
        disabled={disabled}
        sx={{
          marginBottom: '9px',
          [`& .${selectClasses.select}`]: {
            padding: 0,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        renderValue={() => (
          <Box
            className='text-sm'
            sx={{ color: Color.MetricBlue, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
          >
            <MegaphoneIcon className='w-[16px]' />
            <div>Total Impressions</div>
            <InfoTooltip maxWidth={450} title={TooltipContent} />
          </Box>
        )}
        onChange={onChange}
      >
        {options.map((option, i) => (
          <MenuItem key={i} value={option.value}>
            <Checkbox checked={getChecked(option.value)} />
            {option.label}
          </MenuItem>
        ))}
      </Select>

      {loading ? (
        <div className='flex w-full justify-center'>
          <CircularProgress />
        </div>
      ) : (
        <>
          <span className='text-3xl font-bold ml-[20px] mb-[6px]'>{totalImpressions}</span>
          {channelSubLabel && (
            <Typography className='ml-[20px]' sx={{ color: 'text.secondary', fontSize: 12 }}>
              {channelSubLabel}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default FootTrafficImpressionsTotals;
